import React, { useContext } from 'react';

import { Trash } from '@digital-hig/icon/icons/index';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import UserNamesList from '../../../components/container/user-names-list';
import { usePlaceholderImage } from '../../../helpers/getPlaceholderImage';
import I18nContext from '../../contexts/I18nContext';

export interface PlaylistCardProps {
  title: React.ReactNode;
  header: React.ReactNode;
  image?: string;
  titleText?: string;
  url: string;
  bookmark?: React.ReactNode;
  cardId?: string;
  withFallbackImage?: boolean;
  userContentURL?: string;
  userProfileURL?: string;
  isEditMode?: boolean;
  onDelete?: () => void;
}

function PlaylistCard(props: PlaylistCardProps) {
  const {
    title,
    image,
    header,
    titleText,
    url,
    bookmark = null,
    cardId,
    withFallbackImage = false,
    userContentURL,
    userProfileURL,
    isEditMode,
    onDelete,
  } = props;
  const { t } = useContext(I18nContext);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: cardId || '' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Set backup placeholder image if image is not available.
  const placeholderImage = usePlaceholderImage({ id: cardId || '' });
  let imageSrc = image;
  if (withFallbackImage && !image) {
    imageSrc = placeholderImage;
  }
  if (withFallbackImage && !cardId) {
    // eslint-disable-next-line no-console
    console.error("Use of 'withFallbackImage' prop requires a 'cardId'.");
  }

  const titleString =
    titleText || typeof title === 'string' ? (title as string) : '';

  const optimizeImageSrc = (src?: string) => {
    if (!src) return src;
    if (src.includes('//images.ctfassets.net')) {
      const hasQuery = src.includes('?');
      const separator = hasQuery ? '&' : '?';
      return `${src}${separator}w=600`;
    }
    return src;
  };

  return (
    <div
      className="auo-playlist-card !au-text-black md:au-flex au-bg-white au-border au-border-gray-e5 au-rounded-c8 au-p-c20 au-relative"
      style={style}
      {...attributes}
    >
      {imageSrc && (
        <div className="md:au-w-c260 au-max-h-[55vw] md:au-max-h-c160 md:au-mr-c35 au-aspect-video md:au-shrink-0 au-bg-gray-e5 au-flex">
          <img
            className="au-object-cover w-full"
            src={optimizeImageSrc(imageSrc)}
            alt={titleString}
          />{' '}
        </div>
      )}
      <div className="md:au-flex md:au-flex-col md:au-pr-c65 au-py-c15">
        <h5 className="au-uppercase au-text-c14 au-mb-0 au-font-normal au-font-element">
          {header}
        </h5>
        <h3 className="au-text-c26 au-leading-c31 au-mt-c5 au-font-bold au-font-element">
          <a className="au-color-inherit au-decoration-none" href={url}>
            {title}
          </a>
        </h3>
        <div className="au-mt-auto au-pt-c15">
          <UserNamesList
            userContentURL={userContentURL}
            userProfileURL={userProfileURL}
            contentId={cardId}
          />
        </div>
      </div>
      <div className="au-text-right md:au-text-left md:au-absolute md:au-bottom-c25 md:au-right-c25 md:au-top-c25 md:au-flex md:au-flex-col md:au-items-center md:au-py-c15 au-flex au-flex-col au-gap-c20">
        {bookmark}
        {isEditMode && (
          <button
            aria-label={t('playlists.reorder_label', { ns: 'common' })}
            type="button"
            {...listeners}
            ref={setNodeRef}
          >
            <svg fill="none" viewBox="0 0 24 24" height="24px" width="24px">
              <path fill="#333" d="M20 9H4v2h16V9ZM4 15h16v-2H4v2Z" />
            </svg>
          </button>
        )}
        {isEditMode && typeof onDelete === 'function' && (
          <button
            aria-label={t('Delete', { ns: 'common' })}
            type="button"
            onClick={onDelete}
          >
            <Trash width={24} height={24} />
          </button>
        )}
      </div>
    </div>
  );
}

export default PlaylistCard;
